import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Trash2,
  Eye,
  X,
  Mail,
  Phone,
  Loader2,
  Send,
  AlertTriangle,
  CheckCircle2,
  Reply,
  Info,
  Building2,
  Search,
  Filter,
  Tag,
  Sparkles,
} from "lucide-react";
import api from "../../api";

const STATUSES = [
  { id: "all", label: "Alle Status", color: "#64748b" },
  { id: "new", label: "Neu", color: "#E63946" },
  { id: "in_progress", label: "In Bearbeitung", color: "#F59E0B" },
  { id: "done", label: "Beantwortet", color: "#22C55E" },
];

const CATEGORIES = [
  { id: "all", label: "Alle Kategorien", color: "#64748b" },
  { id: "Rechnungen & Finanzen", label: "Rechnungen & Finanzen", color: "#0284c7" },
  { id: "Offerten & Projekte", label: "Offerten & Projekte", color: "#16a34a" },
  { id: "Support & Hosting", label: "Support & Hosting", color: "#d97706" },
  { id: "Allgemein", label: "Allgemein", color: "#64748b" },
];

const TEMPLATES = [
  {
    title: "Rechnung / Zahlung erhalten",
    subject: "Bestätigung zu Ihrer Rechnung",
    message:
      "Sehr geehrte Damen und Herren,\n\nvielen Dank für Ihre Nachricht bezüglich Ihrer Rechnung. Wir haben Ihre Angaben und den Zahlungseingang geprüft und können Ihnen bestätigen, dass alles ordnungsgemäss verbucht ist.\n\nBei weiteren Fragen stehen wir Ihnen jederzeit gerne zur Verfügung.\n\nFreundliche Grüsse",
  },
  {
    title: "Offerte / Projektangebot",
    subject: "Ihr massgeschneidertes Angebot",
    message:
      "Sehr geehrte Damen und Herren,\n\nvielen Dank für Ihr Vertrauen und Ihre Anfrage. Wir haben Ihre Anforderungen eingehend geprüft und unterbreiten Ihnen hiermit unser unverbindliches Angebot.\n\nGerne stehen wir Ihnen für ein persönliches Beratungsgespräch zur Verfügung.\n\nFreundliche Grüsse",
  },
  {
    title: "Support in Bearbeitung",
    subject: "Ihr Support-Ticket ist in Bearbeitung",
    message:
      "Sehr geehrte Damen und Herren,\n\nwir haben Ihre Support-Anfrage dankend erhalten. Unsere IT-Spezialisten analysieren Ihr Anliegen bereits. Sie erhalten in Kürze eine detaillierte Rückmeldung.\n\nFreundliche Grüsse\nIhr Support-Team",
  },
];

const statusInfo = (id) => STATUSES.find((s) => s.id === id) || STATUSES[1];
const categoryInfo = (id) =>
  CATEGORIES.find((c) => c.id === id) || {
    id: id || "Allgemein",
    label: id || "Allgemein",
    color: "#64748b",
  };

export default function Contacts() {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [replies, setReplies] = useState([]);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [replySubject, setReplySubject] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [replyCompanyId, setReplyCompanyId] = useState("");
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [newAlert, setNewAlert] = useState(null);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const knownContactIdsRef = useRef(new Set());

  const playNotificationSound = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const audioCtx = new AudioContext();
      const oscillator = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = 880;
      gain.gain.value = 0.12;
      oscillator.connect(gain);
      gain.connect(audioCtx.destination);
      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        audioCtx.close().catch(() => {});
      }, 180);
    } catch (e) {
      // Audio not supported
    }
  };

  const showBrowserNotification = (title, text) => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "granted") {
      new Notification(title, { body: text });
      return;
    }
    if (Notification.permission !== "denied") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          new Notification(title, { body: text });
        }
      });
    }
  };

  const notifyNewContacts = useCallback((count) => {
    const message = `${count} neue Postfach-Nachricht${count > 1 ? "en" : ""} eingegangen.`;
    setNewAlert({ title: "Neue E-Mail / Nachricht", text: message });
    playNotificationSound();
    showBrowserNotification("Neue Nachricht", message);
    window.setTimeout(() => setNewAlert(null), 8000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [contactsRes, companiesRes] = await Promise.all([
        api.get("/admin/contacts"),
        api.get("/admin/companies"),
      ]);
      setItems(contactsRes.data);
      setCompanies(companiesRes.data);
      knownContactIdsRef.current = new Set(contactsRes.data.map((q) => q.id));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Polling for new incoming messages
  useEffect(() => {
    const intervalId = window.setInterval(async () => {
      try {
        const r = await api.get("/admin/contacts");
        const current = r.data || [];
        const newItems = current.filter((q) => !knownContactIdsRef.current.has(q.id));
        if (newItems.length > 0) {
          setItems(current);
          knownContactIdsRef.current = new Set(current.map((q) => q.id));
          notifyNewContacts(newItems.length);
        } else {
          setItems(current);
          knownContactIdsRef.current = new Set(current.map((q) => q.id));
        }
      } catch (e) {
        // ignore
      }
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [notifyNewContacts]);

  const openContact = async (q) => {
    setSelected(q);
    setReplySubject(q.subject?.startsWith("Re:") ? q.subject : `Re: ${q.subject || ""}`);
    setReplyMessage("");
    setFeedback(null);
    setReplies([]);
    setRepliesLoading(true);

    // Pre-select company sender
    const defaultComp = companies.find((c) => c.isDefault) || companies[0];
    const targetCompId = q.companyId || (defaultComp ? defaultComp.id : "");
    setReplyCompanyId(targetCompId);

    try {
      const r = await api.get(`/admin/contacts/${q.id}/replies`);
      setReplies(r.data);
    } catch (e) {
      // ignore
    } finally {
      setRepliesLoading(false);
    }

    if (q.status === "new") {
      try {
        await api.patch(`/admin/contacts/${q.id}`, { status: "in_progress" });
        setItems((arr) =>
          arr.map((it) => (it.id === q.id ? { ...it, status: "in_progress" } : it))
        );
      } catch (e) {
        // ignore
      }
    }
  };

  const closeModal = () => {
    setSelected(null);
    setReplies([]);
    setFeedback(null);
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/contacts/${id}`, { status });
    setItems((arr) => arr.map((q) => (q.id === id ? { ...q, status } : q)));
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const updateCategory = async (id, category) => {
    await api.patch(`/admin/contacts/${id}`, { category });
    setItems((arr) => arr.map((q) => (q.id === id ? { ...q, category } : q)));
    if (selected?.id === id) setSelected({ ...selected, category });
  };

  const updateCompany = async (id, companyId) => {
    await api.patch(`/admin/contacts/${id}`, { companyId });
    setItems((arr) => arr.map((q) => (q.id === id ? { ...q, companyId } : q)));
    if (selected?.id === id) {
      setSelected({ ...selected, companyId });
      setReplyCompanyId(companyId);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Nachricht wirklich löschen?")) return;
    await api.delete(`/admin/contacts/${id}`);
    setItems((arr) => arr.filter((q) => q.id !== id));
    if (selected?.id === id) closeModal();
  };

  const sendReply = async () => {
    if (!selected) return;
    if (!replySubject.trim() || !replyMessage.trim()) {
      setFeedback({ type: "err", text: "Bitte Betreff und Nachricht ausfüllen." });
      return;
    }
    setSending(true);
    setFeedback(null);

    const activeCompany =
      companies.find((c) => c.id === replyCompanyId) ||
      companies.find((c) => c.isDefault) ||
      companies[0];

    try {
      const r = await api.post(`/admin/contacts/${selected.id}/reply`, {
        subject: replySubject,
        message: replyMessage,
        companyId: replyCompanyId,
      });
      const newReply = r.data;
      setReplies((arr) => [...arr, newReply]);
      setReplyMessage("");

      if (newReply.emailSent) {
        setFeedback({
          type: "ok",
          text: `Antwort erfolgreich versendet über ${newReply.companyName || activeCompany?.name} (${newReply.fromEmail || activeCompany?.email}) an ${selected.email}.`,
        });
        setItems((arr) =>
          arr.map((it) =>
            it.id === selected.id ? { ...it, status: "done", companyId: replyCompanyId } : it
          )
        );
        setSelected({ ...selected, status: "done", companyId: replyCompanyId });
      } else {
        setFeedback({
          type: "err",
          text: `Antwort gespeichert, aber E-Mail konnte nicht übermittelt werden: ${newReply.emailError || "SMTP-Konfiguration prüfen"}.`,
        });
        setItems((arr) =>
          arr.map((it) => (it.id === selected.id ? { ...it, status: "in_progress" } : it))
        );
        setSelected({ ...selected, status: "in_progress" });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Unbekannter Fehler";
      setFeedback({ type: "err", text: `Fehler beim Senden: ${detail}` });
    } finally {
      setSending(false);
    }
  };

  const removeReply = async (rid) => {
    if (!selected) return;
    if (!window.confirm("Antwort wirklich löschen?")) return;
    await api.delete(`/admin/contacts/${selected.id}/replies/${rid}`);
    setReplies((arr) => arr.filter((r) => r.id !== rid));
  };

  const applyTemplate = (t) => {
    setReplySubject(t.subject);
    setReplyMessage(t.message);
  };

  // Filter items
  const filteredItems = items.filter((item) => {
    if (categoryFilter !== "all" && (item.category || "Allgemein") !== categoryFilter) {
      return false;
    }
    if (companyFilter !== "all" && (item.companyId || "") !== companyFilter) {
      return false;
    }
    if (statusFilter !== "all" && item.status !== statusFilter) {
      return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const match =
        (item.fullName || "").toLowerCase().includes(q) ||
        (item.email || "").toLowerCase().includes(q) ||
        (item.subject || "").toLowerCase().includes(q) ||
        (item.message || "").toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  const activeCompany =
    companies.find((c) => c.id === replyCompanyId) ||
    companies.find((c) => c.isDefault) ||
    companies[0];

  return (
    <div data-testid="contacts-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold text-[#0f172a]">
            Postfach & Nachrichten
          </h1>
          <p className="text-[#64748b] mt-1">
            Kategorisierter E-Mail-Posteingang mit Multi-Mandanten-Antwortsystem.
          </p>
        </div>
      </div>

      {/* Real-time alert pill */}
      {newAlert && (
        <div className="fixed top-24 right-4 z-50 w-[340px] rounded-2xl border border-white/20 bg-[#0f172a] p-4 shadow-2xl text-white animate-bounce">
          <div className="flex items-start gap-3">
            <div className="mt-1 h-3 w-3 rounded-full bg-[#E63946]" />
            <div>
              <p className="text-sm font-bold">{newAlert.title}</p>
              <p className="text-xs text-white/80 mt-1 leading-relaxed">{newAlert.text}</p>
            </div>
            <button
              onClick={() => setNewAlert(null)}
              className="ml-auto text-white/80 hover:text-white transition"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Category Pills Bar */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 mt-6">
        {CATEGORIES.map((cat) => {
          const count =
            cat.id === "all"
              ? items.length
              : items.filter((i) => (i.category || "Allgemein") === cat.id).length;
          const isActive = categoryFilter === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => setCategoryFilter(cat.id)}
              className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition flex items-center gap-2 ${
                isActive
                  ? "bg-[#0f172a] text-white shadow-sm"
                  : "bg-white text-slate-700 hover:bg-slate-100 border border-slate-200"
              }`}
            >
              <Tag size={13} style={{ color: isActive ? "#ffffff" : cat.color }} />
              <span>{cat.label}</span>
              <span
                className={`text-[11px] px-1.5 py-0.2 rounded-full font-mono ${
                  isActive ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search & Filters Controls */}
      <div className="bg-white rounded-2xl p-4 shadow-card mt-4 grid sm:grid-cols-3 gap-3">
        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-3 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Suchen nach Name, E-Mail, Betreff..."
            className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-200 focus:border-[#1E88E5] focus:outline-none text-sm text-[#0f172a]"
          />
        </div>

        {/* Company Filter */}
        <div className="relative">
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-[#1E88E5] focus:outline-none text-sm text-[#0f172a] bg-white"
          >
            <option value="all">🏢 Alle Firmen / Mandanten</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.email || "keine E-Mail"})
              </option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-[#1E88E5] focus:outline-none text-sm text-[#0f172a] bg-white"
          >
            {STATUSES.map((st) => (
              <option key={st.id} value={st.id}>
                📌 {st.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Inbox List Table */}
      <div className="bg-white rounded-2xl shadow-card overflow-hidden mt-4">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="animate-spin mx-auto text-[#64748b]" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="p-12 text-center text-[#64748b]">
            Keine Nachrichten in dieser Auswahl vorhanden.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#f8fafc] border-b border-slate-200">
                <tr>
                  <th className="p-4 text-xs uppercase font-bold text-[#64748b]">Absender</th>
                  <th className="p-4 text-xs uppercase font-bold text-[#64748b]">Kategorie</th>
                  <th className="p-4 text-xs uppercase font-bold text-[#64748b]">Betreff</th>
                  <th className="p-4 text-xs uppercase font-bold text-[#64748b]">Firma / Mandant</th>
                  <th className="p-4 text-xs uppercase font-bold text-[#64748b]">Datum</th>
                  <th className="p-4 text-xs uppercase font-bold text-[#64748b]">Status</th>
                  <th className="p-4 text-right text-xs uppercase font-bold text-[#64748b]">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((q) => {
                  const st = statusInfo(q.status);
                  const cat = categoryInfo(q.category);
                  const comp = companies.find((c) => c.id === q.companyId);

                  return (
                    <tr
                      key={q.id}
                      className="border-b border-slate-100 hover:bg-slate-50 transition cursor-pointer"
                      onClick={() => openContact(q)}
                      data-testid={`contact-row-${q.id}`}
                    >
                      <td className="p-4">
                        <div className="font-bold text-[#0f172a]">{q.fullName}</div>
                        <div className="text-xs text-[#64748b]">{q.email}</div>
                      </td>
                      <td className="p-4">
                        <span
                          className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full text-white"
                          style={{ background: cat.color }}
                        >
                          {cat.label}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="text-sm font-semibold text-[#0f172a] max-w-xs truncate">
                          {q.subject}
                        </div>
                        <div className="text-xs text-[#64748b] max-w-xs truncate">
                          {q.message}
                        </div>
                      </td>
                      <td className="p-4 text-xs">
                        {comp ? (
                          <span className="inline-flex items-center gap-1 font-bold text-slate-700 bg-slate-100 px-2 py-1 rounded-lg">
                            <Building2 size={12} className="text-[#1E88E5]" />
                            {comp.name}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">Automatisch</span>
                        )}
                      </td>
                      <td className="p-4 text-xs text-[#64748b] whitespace-nowrap">
                        {new Date(q.createdAt).toLocaleDateString("de-CH", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="p-4">
                        <span
                          className="text-xs font-bold px-2.5 py-1 rounded-full text-white whitespace-nowrap"
                          style={{ background: st.color }}
                        >
                          {st.label}
                        </span>
                      </td>
                      <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => openContact(q)}
                            data-testid={`view-contact-${q.id}`}
                            className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-[#1E88E5] hover:text-white flex items-center justify-center transition"
                            title="Öffnen & Antworten"
                          >
                            <Eye size={15} />
                          </button>
                          <button
                            onClick={() => remove(q.id)}
                            data-testid={`delete-contact-${q.id}`}
                            className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-[#E63946] hover:text-white flex items-center justify-center transition"
                            title="Löschen"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail & Reply Modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-xs"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="contact-detail-modal"
          >
            {/* Modal Header */}
            <div className="bg-[#0f172a] text-white p-6 rounded-t-2xl flex items-start justify-between sticky top-0 z-10 border-b border-slate-800">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[#FFC107] text-xs font-bold tracking-wider uppercase">
                    POSTFACH-NACHRICHT
                  </span>
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
                    style={{ background: categoryInfo(selected.category).color }}
                  >
                    {categoryInfo(selected.category).label}
                  </span>
                </div>
                <h2 className="text-2xl font-extrabold mt-1 text-white">{selected.subject}</h2>
                <p className="text-white/70 text-xs mt-1">
                  Eingegangen am{" "}
                  {new Date(selected.createdAt).toLocaleString("de-CH", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition"
                data-testid="close-contact-modal"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Sender Details & Management Bar */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Left: Contact Info */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-[#64748b] uppercase mb-1">
                    Absender-Informationen
                  </p>
                  <p className="font-extrabold text-[#0f172a] text-base">{selected.fullName}</p>
                  <a
                    href={`mailto:${selected.email}`}
                    className="text-sm text-[#1E88E5] flex items-center gap-1.5 mt-1 font-semibold hover:underline"
                  >
                    <Mail size={14} /> {selected.email}
                  </a>
                  {selected.phone && (
                    <a
                      href={`tel:${selected.phone}`}
                      className="text-sm text-[#64748b] flex items-center gap-1.5 mt-1 hover:underline"
                    >
                      <Phone size={14} /> {selected.phone}
                    </a>
                  )}
                </div>

                {/* Right: Category & Company Assignment */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2.5">
                  <div>
                    <label className="block text-xs font-bold text-[#64748b] uppercase mb-1">
                      Kategorie zuweisen
                    </label>
                    <select
                      value={selected.category || "Allgemein"}
                      onChange={(e) => updateCategory(selected.id, e.target.value)}
                      className="w-full text-xs font-bold p-2 rounded-lg border border-slate-200 bg-white text-[#0f172a]"
                    >
                      {CATEGORIES.filter((c) => c.id !== "all").map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#64748b] uppercase mb-1">
                      Zugeordnete Firma / Mandant
                    </label>
                    <select
                      value={selected.companyId || ""}
                      onChange={(e) => updateCompany(selected.id, e.target.value)}
                      className="w-full text-xs font-bold p-2 rounded-lg border border-slate-200 bg-white text-[#0f172a]"
                    >
                      <option value="">Standard (Automatisch)</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.email || "keine E-Mail"})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Original Message */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                <p className="text-xs font-bold text-[#64748b] uppercase mb-2">
                  Original-Nachricht vom Kunden
                </p>
                <p className="text-sm text-[#0f172a] whitespace-pre-wrap leading-relaxed">
                  {selected.message}
                </p>
              </div>

              {/* Status Switcher */}
              <div className="flex items-center justify-between border-y border-slate-200 py-3">
                <span className="text-xs font-bold text-[#64748b] uppercase">Status ändern</span>
                <div className="flex gap-2">
                  {STATUSES.filter((s) => s.id !== "all").map((s) => (
                    <button
                      key={s.id}
                      onClick={() => updateStatus(selected.id, s.id)}
                      data-testid={`status-${s.id}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                        selected.status === s.id ? "text-white" : "bg-slate-100 text-[#0f172a]"
                      }`}
                      style={selected.status === s.id ? { background: s.color } : {}}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Thread History */}
              <div>
                <p className="text-xs font-bold text-[#64748b] uppercase mb-3">
                  Bisheriger Verlauf & Antworten ({replies.length})
                </p>

                {repliesLoading ? (
                  <div className="py-6 text-center">
                    <Loader2 className="animate-spin mx-auto text-[#64748b]" size={18} />
                  </div>
                ) : replies.length === 0 ? (
                  <div className="text-xs text-[#64748b] bg-slate-50 rounded-xl p-4 text-center border border-dashed border-slate-200">
                    Noch keine Antwort über das Admin-Panel gesendet.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {replies.map((r) => (
                      <div
                        key={r.id}
                        className="border border-slate-200 rounded-xl p-4 bg-white shadow-xs"
                        data-testid={`reply-${r.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-bold text-[#0f172a]">{r.sentBy}</span>
                              {r.companyName && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#1E88E5] bg-blue-50 px-2 py-0.5 rounded-md">
                                  <Building2 size={11} /> {r.companyName}
                                  {r.fromEmail ? ` (${r.fromEmail})` : ""}
                                </span>
                              )}
                              <span className="text-xs text-[#64748b]">
                                {new Date(r.createdAt).toLocaleString("de-CH")}
                              </span>
                              {r.emailSent ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                                  <CheckCircle2 size={11} /> E-Mail versendet
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                  <AlertTriangle size={11} /> Nicht versendet
                                </span>
                              )}
                            </div>
                            <div className="mt-2 text-sm font-semibold text-[#0f172a]">
                              {r.subject}
                            </div>
                            <p className="text-sm text-[#334155] mt-1 whitespace-pre-wrap leading-relaxed">
                              {r.message}
                            </p>
                            {r.emailError && (
                              <p className="text-xs text-[#E63946] mt-2">
                                Sendefehler: {r.emailError}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => removeReply(r.id)}
                            data-testid={`delete-reply-${r.id}`}
                            className="shrink-0 w-7 h-7 rounded-lg bg-slate-100 hover:bg-[#E63946] hover:text-white flex items-center justify-center transition"
                            title="Antwort löschen"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reply Composer Form */}
              <div className="border-t border-slate-200 pt-5 space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-xs font-bold text-[#64748b] uppercase flex items-center gap-2">
                    <Reply size={14} className="text-[#E63946]" />
                    Antwort verfassen an:{" "}
                    <span className="text-[#0f172a] font-mono">{selected.email}</span>
                  </p>

                  {/* Template Quick Actions */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] font-bold text-slate-400 flex items-center gap-1">
                      <Sparkles size={11} /> Vorlagen:
                    </span>
                    {TEMPLATES.map((t, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => applyTemplate(t)}
                        className="text-[11px] font-bold px-2 py-1 rounded bg-slate-100 hover:bg-[#1E88E5] hover:text-white text-slate-700 transition"
                      >
                        {t.title}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Absender-Firma Selector (Dynamic Multi-Company Sender) */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-1.5">
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#64748b]">
                    Absender-Firma & E-Mail-Adresse für diese Antwort:
                  </label>
                  <select
                    value={replyCompanyId}
                    onChange={(e) => setReplyCompanyId(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 font-semibold text-sm bg-white text-[#0f172a] focus:border-[#1E88E5] focus:outline-none"
                  >
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} — Absender: {c.email || "System-Standard"}
                        {c.smtpHost ? ` (Eigener SMTP: ${c.smtpHost})` : ""}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-[#64748b]">
                    Diese Antwort wird mit dem Briefkopf von{" "}
                    <b>{activeCompany?.name || "RedWORK"}</b> versendet und als Absender-Adresse{" "}
                    <b>{activeCompany?.email || "info@redwork.ch"}</b> verwenden.
                  </p>
                </div>

                {/* Reply Subject */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#64748b] mb-1">
                    Betreff
                  </label>
                  <input
                    type="text"
                    value={replySubject}
                    onChange={(e) => setReplySubject(e.target.value)}
                    placeholder="Betreff der E-Mail"
                    data-testid="reply-subject-input"
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-[#1E88E5] focus:outline-none text-sm bg-white text-[#0f172a]"
                  />
                </div>

                {/* Reply Message */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#64748b] mb-1">
                    Nachrichtentext
                  </label>
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder={`Guten Tag ${selected.fullName?.split(" ")[0] || ""},\n\nvielen Dank für Ihre Anfrage...`}
                    rows={7}
                    data-testid="reply-message-input"
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-[#1E88E5] focus:outline-none text-sm bg-white text-[#0f172a] resize-y"
                  />
                </div>

                {/* Feedback Alert */}
                {feedback && (
                  <div
                    data-testid="reply-feedback"
                    className={`flex items-start gap-2 text-xs rounded-xl p-3.5 ${
                      feedback.type === "ok"
                        ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
                        : "bg-red-50 border border-red-200 text-red-800"
                    }`}
                  >
                    {feedback.type === "ok" ? (
                      <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-600" />
                    ) : (
                      <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
                    )}
                    <span className="font-medium leading-relaxed">{feedback.text}</span>
                  </div>
                )}

                {/* Submit Action */}
                <div className="flex items-center justify-between gap-3 pt-2">
                  <span className="text-xs text-[#64748b]">
                    Versand erfolgt direkt über Schweizer SMTP
                  </span>
                  <button
                    onClick={sendReply}
                    disabled={sending || !replyMessage.trim() || !replySubject.trim()}
                    data-testid="send-reply-button"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#E63946] hover:bg-[#c5303d] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm transition shadow-md"
                  >
                    {sending ? (
                      <Loader2 size={15} className="animate-spin" />
                    ) : (
                      <Send size={15} />
                    )}
                    {sending ? "Wird versendet..." : "Antwort jetzt senden"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
